<template>
  <!-- display: contents — кнопка встаёт в ряд с полем ввода, а список
       выбранных файлов позиционируется от самого ряда (у него position: relative) -->
  <div class="contents">
    <button
      type="button"
      class="flex size-[22px] shrink-0 items-center justify-center text-txopacity transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="disabled || items.length >= CHAT_FILES_LIMIT"
      title="Прикрепить файл"
      @click="fileInput?.click()"
    >
      <Icon name="lucide:paperclip" size="22" class="block" />
    </button>

    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      @change="onPick"
    />

    <div
      v-if="items.length"
      class="absolute bottom-full left-0 right-0 z-10 mb-2 flex flex-wrap gap-2 rounded-2xl border border-[#E9E6F3] bg-white p-2"
    >
      <div
        v-for="item in items"
        :key="item.id"
        class="flex max-w-full items-center gap-2 rounded-xl bg-bgnew py-1.5 pl-2 pr-1.5"
      >
        <img
          v-if="item.previewUrl"
          :src="item.previewUrl"
          alt=""
          class="size-8 shrink-0 rounded-lg object-cover"
        />
        <Icon v-else name="lucide:file" size="18" class="shrink-0 text-txopacity" />

        <span class="min-w-0 max-w-[160px] truncate text-xs">{{ item.name }}</span>

        <Icon
          v-if="item.isUploading"
          name="lucide:loader-circle"
          size="14"
          class="block shrink-0 animate-spin text-txopacity"
        />
        <button
          type="button"
          class="flex size-[18px] shrink-0 items-center justify-center text-txopacity hover:text-txred"
          @click="remove(item.id)"
        >
          <Icon name="lucide:x" size="14" class="block" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import ChatFileService, { type ChatSide } from "~/services/ChatFileService";
  import { CHAT_FILES_LIMIT, CHAT_FILE_MAX_SIZE, chatFileSize } from "~/common/chatFiles";
  import type { IChatFile } from "~/interfaces/chat";

  interface PendingAttachment {
    id: number;
    name: string;
    previewUrl: string;
    isUploading: boolean;
    file: IChatFile | null;
  }

  const props = defineProps<{
    // От стороны зависит, каким токеном грузим файл в медиа-хранилище
    side: ChatSide;
    disabled?: boolean;
  }>();

  const fileInput = ref<HTMLInputElement | null>(null);
  const items = ref<PendingAttachment[]>([]);

  let nextId = 0;

  const onPick = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []);
    input.value = "";
    if (picked.length) await addFiles(picked);
  };

  const addFiles = async (picked: File[]) => {
    const free = CHAT_FILES_LIMIT - items.value.length;
    if (free <= 0) {
      ElMessage.warning(`К сообщению можно приложить не более ${CHAT_FILES_LIMIT} файлов`);
      return;
    }
    if (picked.length > free) {
      ElMessage.warning(`К сообщению можно приложить не более ${CHAT_FILES_LIMIT} файлов`);
    }

    const accepted = picked.slice(0, free).filter((file) => {
      if (file.size > CHAT_FILE_MAX_SIZE) {
        ElMessage.error(
          `Файл «${file.name}» больше ${chatFileSize(CHAT_FILE_MAX_SIZE)} и не будет отправлен`
        );
        return false;
      }
      return true;
    });

    // Последовательно: хранилище отбивает пачку одновременных загрузок 429-м
    for (const file of accepted) {
      await upload(file);
    }
  };

  const upload = async (file: File) => {
    const id = (nextId += 1);

    items.value.push({
      id,
      name: file.name,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      isUploading: true,
      file: null,
    });

    // Правим элемент через массив, а не по ссылке на исходный объект: мутация
    // «сырого» объекта в обход reactive-прокси не перерисовывает список.
    // Файл могли удалить, пока он грузился — тогда обновлять нечего.
    const patch = (changes: Partial<PendingAttachment>) => {
      const target = items.value.find((item) => item.id === id);
      if (target) Object.assign(target, changes);
    };

    try {
      const uploaded = await ChatFileService.upload(file, props.side);
      if (!uploaded) throw new Error("upload failed");
      patch({ file: uploaded, isUploading: false });
    } catch (error: any) {
      console.error("ChatAttachButton - upload", error);
      // Незагруженный файл в списке не держим: отправлять из него нечего
      remove(id);
      const status = error?.statusCode ?? error?.response?.status;
      ElMessage.error(
        status === 429
          ? "Слишком много загрузок подряд, попробуйте ещё раз через минуту"
          : `Не удалось загрузить файл «${file.name}»`
      );
    }
  };

  const remove = (id: number) => {
    const index = items.value.findIndex((item) => item.id === id);
    if (index === -1) return;
    if (items.value[index].previewUrl) URL.revokeObjectURL(items.value[index].previewUrl);
    items.value.splice(index, 1);
  };

  const reset = () => {
    items.value.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    items.value = [];
  };

  onBeforeUnmount(reset);

  const files = computed<IChatFile[]>(() =>
    items.value.map((item) => item.file).filter(Boolean) as IChatFile[]
  );

  const isUploading = computed(() => items.value.some((item) => item.isUploading));

  defineExpose({ files, isUploading, reset });
</script>
